import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

/**
 * Expense Action Handler Page
 * Handles approve/reject actions from email links
 * - If user not logged in: Save action to localStorage and redirect to login
 * - If user logged in: Perform action immediately
 */
export default function ExpenseAction() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const expenseId = searchParams.get("expenseId");
  const action = searchParams.get("action"); // "approve" or "reject"

  useEffect(() => {
    // Wait for auth to initialize
    if (authLoading) {
      return;
    }

    const handleAction = async () => {
      // Check if there's a pending action from localStorage (user logged in after clicking email link)
      const pendingActionStr = localStorage.getItem("pendingExpenseAction");
      let finalExpenseId = expenseId;
      let finalAction = action;

      if (pendingActionStr && user) {
        try {
          const pendingAction = JSON.parse(pendingActionStr);
          if (pendingAction.type === "expense_action") {
            console.log(
              "Found pending expense action from localStorage:",
              pendingAction
            );
            finalExpenseId = pendingAction.expenseId;
            finalAction = pendingAction.action;
            // Clear the pending action since we're processing it
            localStorage.removeItem("pendingExpenseAction");
          }
        } catch (err) {
          console.error("Failed to parse pending action:", err);
        }
      }

      if (!finalExpenseId || !finalAction) {
        setError("Invalid action link. Missing expense ID or action.");
        return;
      }

      if (finalAction !== "approve" && finalAction !== "reject") {
        setError("Invalid action. Must be 'approve' or 'reject'.");
        return;
      }

      // If user is not logged in, save action and redirect to login
      if (!user) {
        console.log(
          "User not logged in, saving expense action to localStorage"
        );
        const pendingAction = {
          type: "expense_action",
          expenseId: finalExpenseId,
          action: finalAction,
          timestamp: Date.now(),
        };
        localStorage.setItem(
          "pendingExpenseAction",
          JSON.stringify(pendingAction)
        );
        toast.info("Please log in to complete this action");
        navigate("/login?redirect=/expense-action", { replace: true });
        return;
      }

      // User is logged in, perform action immediately
      setProcessing(true);
      try {
        const endpoint = `/expenses/${finalExpenseId}/${finalAction}`;
        // Send payload matching backend expectations
        // Backend expects: approve -> no body, reject -> { reason }
        const payload = finalAction === "reject" ? { reason: "" } : {};

        console.log(
          `Processing ${finalAction} for expense ${finalExpenseId}`,
          payload
        );
        const response = await api.put(endpoint, payload);
        console.log("Response received:", response.data);

        // API interceptor unwraps the response, so response.data is the expense object
        // If we get here without an error, the request was successful
        if (response.data) {
          toast.success(
            finalAction === "approve"
              ? "Expense request approved successfully!"
              : "Expense request rejected successfully!"
          );

          // Clear any pending action (already cleared above, but ensure it's cleared)
          localStorage.removeItem("pendingExpenseAction");

          // Redirect to expenses management page
          setTimeout(() => {
            navigate("/expenses", { replace: true });
          }, 1500);
        } else {
          throw new Error("Failed to process expense action");
        }
      } catch (err) {
        // Handle error response - check both wrapped and unwrapped formats
        let errorMessage = "Failed to process expense action";

        if (err.response?.data) {
          // Check if error is wrapped in { success: false, error: "..." }
          if (err.response.data.error) {
            errorMessage = err.response.data.error;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (typeof err.response.data === "string") {
            errorMessage = err.response.data;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        toast.error(errorMessage);
        console.error("Expense action error:", err);
        console.error("Error response:", err.response?.data);
      } finally {
        setProcessing(false);
      }
    };

    handleAction();
  }, [expenseId, action, user, authLoading, navigate]);

  // Show loading state
  if (authLoading || processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 shadow-soft-lg max-w-md w-full mx-4"
        >
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {processing ? "Processing..." : "Loading..."}
            </h2>
            <p className="text-gray-600">
              {processing
                ? `Please wait while we ${
                    action || "process"
                  } the expense request...`
                : "Checking authentication..."}
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 shadow-soft-lg max-w-md w-full mx-4"
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate("/login")}
              className="btn-primary w-full"
            >
              Go to Login
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
}

